import { Component, type ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * A lazy() route chunk that failed to load — usually a stale hashed filename
 * left over from a previous deploy. Wording differs per browser, so match on
 * the recognisable fragments rather than one exact string.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'expected a javascript-or-wasm module script',
  'chunkloaderror',
];

function isChunkLoadError(error?: Error): boolean {
  const message = `${error?.name ?? ''} ${error?.message ?? ''}`.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
    // In production, you might want to log to an error tracking service
    if (import.meta.env.PROD) {
      // e.g., Sentry.captureException(error, { extra: errorInfo });
    }
  }

  private handleReset = () => {
    // A failed module fetch is cached by the document's module map AND by the
    // rejected promise React.lazy holds on to, so re-rendering retries nothing.
    // Only a fresh page load can recover — and it also picks up the current
    // index.html with the chunk names of the live deploy.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }

    this.setState({ hasError: false, error: undefined });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isStaleChunk = isChunkLoadError(this.state.error);

      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 px-4">
          <div className="text-center max-w-md">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {isStaleChunk ? 'A new version is available' : 'Something went wrong'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {isStaleChunk
                ? 'This page was updated while you had it open. Reload to get the latest version.'
                : 'We apologize for the inconvenience. Please try refreshing the page or go back to the dashboard.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleReset} variant="primary">
                {isStaleChunk ? 'Reload' : 'Try Again'}
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
              >
                Go Home
              </Button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <div className="mt-6 p-4 bg-red-50 dark:bg-red-950/20 rounded-lg text-left overflow-auto">
                <p className="text-sm font-mono text-red-800 dark:text-red-200">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
